import { useEffect } from "react";
import chevronLeft from "@assets/icon-chevron-left-white.svg";
import chevronRight from "@assets/icon-chevron-right-white.svg";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import { useDelayedComponents } from "@hooks/useDelayedComponents";
import { cn } from "@util/helpers";
import Cookies from "js-cookie";
import { motion } from "motion/react";

type PulseDesktopCarouselButtonProps = {
  /** Index of the active article card */
  activeArticle: number;
  /** Indicates if autoplay is currently displayed */
  isDisplayAutoplay: boolean;
  /** Callback to handle navigating to the left card */
  handleNavigateLeftCard: () => void;
  /** Callback to handle navigating to the right card */
  handleNavigateRightCard: () => void;
  /** Callback to reset autoplay to the left */
  resetAutoplayLeft: () => void;
  /** Title to be capture for ga tagging*/
  displayTitle?: string;
};

/** Carousel button for pulse variant manual and autoplay */
const PulseDesktopCarouselButton = ({
  activeArticle,
  handleNavigateLeftCard,
  handleNavigateRightCard,
  resetAutoplayLeft,
  isDisplayAutoplay,
  displayTitle = "",
}: PulseDesktopCarouselButtonProps): React.ReactElement => {
  const { isShown, handleSetIsShown } = useDelayedComponents(5000);

  const { sendCustomEvent } = useSendPulseGaData();

  useEffect(() => {
    if (isDisplayAutoplay) return;

    if (!isShown) return;
    const timer = setTimeout(() => {
      handleSetIsShown(false);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [isShown, handleSetIsShown, isDisplayAutoplay]);

  useEffect(() => {
    const cookie = Cookies.get("bt-pulse-onboarding");
    if (typeof cookie === "undefined") return;

    handleSetIsShown(false);
  }, [handleSetIsShown]);

  const variants = {
    initial: { rotate: 0 },
    start: { rotate: 90 },
    hoverState: { scale: 0.9 },
    hoverTextInitial: { opacity: 0 },
    hoverTextStart: { opacity: 1 },
  };

  const handleArrowRightClick = () => {
    handleNavigateRightCard();
    resetAutoplayLeft();

    sendCustomEvent(`desktop::click >::${activeArticle}`, displayTitle);
  };

  const handleArrowLeftClick = () => {
    handleNavigateLeftCard();
    resetAutoplayLeft();

    sendCustomEvent(`desktop::click <::${activeArticle}`, displayTitle);
  };

  return (
    <div
      className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 justify-between"
      data-testid="pulse-desktop-carousel-button-component"
    >
      <button
        onClick={handleArrowLeftClick}
        className={cn(
          "group relative -left-[250px] flex h-[60px] w-[60px] items-center justify-center rounded-full text-white",
          { "pointer-events-none opacity-0": activeArticle === 1 }
        )}
        data-testid="pulse-desktop-carousel-button-left-component"
      >
        <div className="absolute top-0 -z-10 h-[60px] w-[60px] rounded-full bg-pulseButton transition-all duration-500 group-hover:animate-pulseGradientButtonAnimation" />

        <div className="absolute -bottom-12 w-max rounded-[0.25rem] bg-[#474747] px-3 py-[6px] font-poppins text-4xs tracking-2% text-white opacity-0 transition-opacity duration-300 group-hover:!opacity-100">
          previous article
        </div>

        <div className="flex aspect-square h-[57px] justify-center rounded-full bg-[#1a1a1a] group-hover:bg-[#474747]">
          <img
            src={chevronLeft}
            alt="pulse left arrow"
            width={24}
            height={24}
          />
        </div>
      </button>

      <button
        onClick={handleArrowRightClick}
        className={
          "group relative -right-[250px] flex h-[60px] w-[60px] items-center justify-center rounded-full text-white"
        }
        data-testid="pulse-desktop-carousel-button-right-component"
      >
        <motion.div
          variants={variants}
          animate={isShown && !isDisplayAutoplay ? "start" : "initial"}
          className="absolute top-0 -z-10 h-[60px] w-[60px] rounded-full bg-pulseButton transition-all duration-500 group-hover:animate-pulseGradientButtonAnimation"
        />

        <motion.div
          variants={variants}
          animate={
            isShown && !isDisplayAutoplay
              ? "hoverTextStart"
              : "hoverTextInitial"
          }
          className="absolute -bottom-12 w-max rounded-[0.25rem] bg-[#474747] px-3 py-[6px] font-poppins text-4xs tracking-2% text-white opacity-0 transition-opacity duration-300 group-hover:!opacity-100"
        >
          next article
        </motion.div>

        <div className="flex aspect-square h-[57px] justify-center rounded-full bg-[#1a1a1a] group-hover:bg-[#474747]">
          <img
            src={chevronRight}
            alt="pulse left arrow"
            width={24}
            height={24}
          />
        </div>
      </button>
    </div>
  );
};

export default PulseDesktopCarouselButton;
